import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * UnifiedMyTable — default export is the standalone route wrapper
 * (/unified/my-table with AppShell), named export MyTablePanel is the
 * dashboard-embedded panel with no shell. Both render the same body.
 *
 * Replaces the recovery MyTableScreen. Three surfaces: Connections,
 * Invites (in + out), Discovery (suggested folks with overlapping dietary
 * context). Actions link out to /unified/table-questions and
 * /unified/safety-signals so each feature has one canonical home.
 *
 * Backend stubs (getMyTableConnections, etc) all reject; we keep
 * optimistic state seeded with demo data so the UI is meaningful for
 * design review.
 */

const EASE = [0.22, 1, 0.36, 1];

const DEMO_CONNECTIONS = [
  { id: 'c1', name: 'Maya Patel',     relation: 'Friend',  dietary: ['Gluten-Free', 'Vegetarian'], visits_together: 14, last: '3 days ago' },
  { id: 'c2', name: 'Jordan Lee',     relation: 'Partner', dietary: ['Dairy-Free'],                 visits_together: 42, last: 'yesterday' },
  { id: 'c3', name: 'Sarah Martinez', relation: 'Sibling', dietary: ['Peanut allergy'],             visits_together: 6,  last: '2 weeks ago' },
];
const DEMO_INVITES_IN = [
  { id: 'i1', direction: 'incoming', from: 'Alex Chen', email: 'alex@example.com', message: "Hey! Saw you loved Il Violino — want to trade allergy picks?", created: '2 hours ago' },
];
const DEMO_INVITES_OUT = [
  { id: 'i2', direction: 'outgoing', to: 'Riley Thompson', email: 'riley@example.com', created: '4 days ago' },
];
const DEMO_SUGGESTIONS = [
  { id: 's1', name: 'Noor Al-Said',          overlap: ['Peanut allergy', 'Dairy-Free'], mutuals: 2, city: 'New York' },
  { id: 's2', name: 'Taylor Brooks',         overlap: ['Gluten-Free'],                   mutuals: 1, city: 'Brooklyn' },
  { id: 's3', name: 'Priya Balasubramanian', overlap: ['Vegetarian', 'Jain'],            mutuals: 3, city: 'Queens' },
];

const TABS = [
  { id: 'connections', label: 'Connections', icon: 'people' },
  { id: 'invites',     label: 'Invites',     icon: 'mail' },
  { id: 'discovery',   label: 'Discovery',   icon: 'compass' },
];

const AVATAR_HUES = [
  { bg: 'linear-gradient(160deg, #1E9BFF, #0F7FE8)' },
  { bg: 'linear-gradient(160deg, #FF6B8A, #E91E63)' },
  { bg: 'linear-gradient(160deg, #7C3AED, #5B21B6)' },
  { bg: 'linear-gradient(160deg, #F59E0B, #D97706)' },
  { bg: 'linear-gradient(160deg, #10B981, #059669)' },
  { bg: 'linear-gradient(160deg, #EC4899, #BE185D)' },
];

function hueFor(name) {
  if (!name) return AVATAR_HUES[0];
  const sum = Array.from(name).reduce((s, c) => s + c.charCodeAt(0), 0);
  return AVATAR_HUES[sum % AVATAR_HUES.length];
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

// --- Standalone route ---
export default function UnifiedMyTable() {
  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '28px 20px 4px', position: 'relative', zIndex: 1 }}>
        <motion.section
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.46, ease: EASE }}
          style={{ textAlign: 'center', marginBottom: 20 }}
        >
          <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
            <PeopleIcon size={12} /> MY TABLE
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.025em', color: 'var(--cwm-ink)', margin: '0 0 8px' }}>
            Build your <em style={{ fontStyle: 'italic', color: 'var(--cwm-blue-deep)' }}>dining circle</em>.
          </h1>
          <p style={{ maxWidth: 560, margin: '0 auto', fontSize: 15, fontWeight: 500, color: 'var(--cwm-gray-700)' }}>
            Connect with diners who share your allergies, preferences, or favorite spots. Safer together.
          </p>
        </motion.section>
      </div>
      <MyTablePanel userId={Number(localStorage.getItem('userId')) || null} />
    </AppShell>
  );
}

// --- Dashboard-embedded panel ---
export function MyTablePanel({ userId }) {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [invitesIn,   setInvitesIn]   = useState([]);
  const [invitesOut,  setInvitesOut]  = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('connections');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // All four calls need userId now (backend uses it to scope the
        // query). If userId is missing we skip the network entirely
        // and fall through to demo seeds.
        const [conns, invs, sugs] = userId
          ? await Promise.all([
              dataService.getMyTableConnections(userId).catch(() => null),
              dataService.getMyTableInvites(userId).catch(() => null),
              dataService.getMyTableSuggestions(userId).catch(() => null),
            ])
          : [null, null, null];
        if (cancelled) return;
        setConnections(Array.isArray(conns) && conns.length ? conns : DEMO_CONNECTIONS);
        const invList = Array.isArray(invs) ? invs : [];
        setInvitesIn(invList.length ? invList.filter((i) => i.direction === 'incoming') : DEMO_INVITES_IN);
        setInvitesOut(invList.length ? invList.filter((i) => i.direction === 'outgoing') : DEMO_INVITES_OUT);
        setSuggestions(Array.isArray(sugs) && sugs.length ? sugs : DEMO_SUGGESTIONS);
      } catch {
        if (!cancelled) {
          setConnections(DEMO_CONNECTIONS);
          setInvitesIn(DEMO_INVITES_IN);
          setInvitesOut(DEMO_INVITES_OUT);
          setSuggestions(DEMO_SUGGESTIONS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const flash = (tone, text) => {
    setAlert({ tone, text, id: Date.now() });
    setTimeout(() => setAlert(null), 3000);
  };

  const stats = useMemo(() => ({
    connections: connections.length,
    pending: invitesIn.length + invitesOut.length,
    answered: 12,
  }), [connections, invitesIn, invitesOut]);

  const sendInvite = async ({ email, message }) => {
    // Try the real POST first and prefer the server-hydrated row (it has
    // a real db_id for later accept/decline). Fall back to a local optimistic
    // row if the network fails so design-review continues working.
    let saved = null;
    try {
      if (userId) saved = await dataService.sendMyTableInvite(userId, { email, message });
    } catch {}
    setInvitesOut((prev) => [...prev, saved || {
      id: `i-${Date.now()}`, direction: 'outgoing',
      to: email.split('@')[0].replace(/\./g, ' '), email, created: 'just now',
    }]);
    setInviteModalOpen(false);
    flash('green', `Invite sent to ${email}.`);
  };

  const acceptInvite = async (inv) => {
    // Real accept needs the integer `db_id`. Demo-seeded rows don't have
    // one; we skip the network for those and update state optimistically.
    try { if (inv.db_id && userId) await dataService.acceptMyTableInvite(inv.db_id, userId); } catch {}
    setInvitesIn((prev) => prev.filter((i) => i.id !== inv.id));
    setConnections((prev) => [...prev, {
      id: `c-${inv.id}`, name: inv.from, relation: 'New connection',
      dietary: [], visits_together: 0, last: 'just added',
    }]);
    flash('green', `Connected with ${inv.from}.`);
  };

  const declineInvite = async (inv) => {
    try { if (inv.db_id) await dataService.declineMyTableInvite(inv.db_id); } catch {}
    setInvitesIn((prev) => prev.filter((i) => i.id !== inv.id));
    flash('red', `Declined invite from ${inv.from}.`);
  };

  const removeConnection = (conn) => {
    if (!window.confirm(`Remove ${conn.name} from your table?`)) return;
    setConnections((prev) => prev.filter((c) => c.id !== conn.id));
    flash('red', `${conn.name} removed.`);
  };

  const inviteSuggestion = (sug) => {
    setInvitesOut((prev) => [...prev, {
      id: `i-${Date.now()}`, direction: 'outgoing',
      to: sug.name, email: `${sug.name.toLowerCase().replace(/\s+/g, '.')}@example.com`, created: 'just now',
    }]);
    setSuggestions((prev) => prev.filter((s) => s.id !== sug.id));
    flash('green', `Invite sent to ${sug.name}.`);
  };

  if (loading) return <LoadingBlock />;

  return (
    <div style={{ padding: '18px 20px' }}>

      <AnimatePresence>
        {alert && (
          <motion.div
            key={alert.id}
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            style={{
              padding: '10px 14px', borderRadius: 14, marginBottom: 14,
              background: alert.tone === 'red' ? 'var(--cwm-red-light)' : 'var(--cwm-green-light)',
              color: alert.tone === 'red' ? 'var(--cwm-red-text)' : 'var(--cwm-green-text)',
              fontSize: 13, fontWeight: 800,
            }}
          >{alert.text}</motion.div>
        )}
      </AnimatePresence>

      {/* Purple hero stats strip */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, ease: EASE }}
        style={{
          position: 'relative', overflow: 'hidden',
          padding: '22px 24px', borderRadius: 24,
          background: 'linear-gradient(160deg, #7C3AED 0%, #5B21B6 60%, #3B1E80 100%)',
          color: '#fff', marginBottom: 18,
          boxShadow: '0 20px 44px rgba(124,58,237,0.32)',
        }}
      >
        <div aria-hidden style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0, rgba(255,255,255,0.18) 1.5px, transparent 1.6px)',
          backgroundSize: '22px 22px', opacity: 0.4,
        }}/>
        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.82 }}>
              MY TABLE
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.015em', marginTop: 4 }}>
              {stats.connections === 0 ? 'Start building your table' : `${stats.connections} people in your circle`}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.9, marginTop: 4, maxWidth: 440 }}>
              Connect with other diners who share allergies, preferences, or favorite spots. You're safer together.
            </div>
          </div>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: 'rgba(255,255,255,0.18)',
            display: 'grid', placeItems: 'center',
            backdropFilter: 'blur(6px)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.22)',
          }}>
            <PeopleIcon size={24} />
          </div>
        </div>
        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 18 }}>
          <StatTile label="Connections"     value={stats.connections} />
          <StatTile label="Pending invites" value={stats.pending} />
          <StatTile label="Answers given"   value={stats.answered} />
        </div>
      </motion.div>

      {/* Action pills */}
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.46, delay: 0.08, ease: EASE }}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 22 }}
      >
        <ActionPill tone="blue"   icon="plus"   label="Send invite"          onClick={() => setInviteModalOpen(true)} />
        <ActionPill tone="green"  icon="chat"   label="Ask the table"        onClick={() => navigate('/unified/table-questions')} />
        <ActionPill tone="orange" icon="shield" label="Share safety signal"  onClick={() => navigate('/unified/safety-signals')} />
      </motion.div>

      {/* Sub-tabs */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 14, padding: 4,
        background: 'var(--cwm-gray-50)', borderRadius: 999, border: '1px solid var(--cwm-gray-200)',
      }}>
        {TABS.map((t) => {
          const on = activeTab === t.id;
          const count =
            t.id === 'connections' ? connections.length :
            t.id === 'invites'     ? invitesIn.length + invitesOut.length :
            suggestions.length;
          return (
            <button key={t.id} type="button" onClick={() => setActiveTab(t.id)}
                    style={{
                      flex: 1,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '10px 14px', borderRadius: 999,
                      background: on ? '#fff' : 'transparent',
                      color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-700)',
                      border: 'none', cursor: 'pointer',
                      fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 900,
                      boxShadow: on ? '0 4px 12px rgba(10,37,64,0.08)' : 'none',
                      transition: 'all 180ms ease',
                    }}>
              <TabIcon name={t.icon} size={14} /> {t.label}
              <span style={{
                padding: '1px 8px', borderRadius: 999,
                background: on ? 'var(--cwm-blue-pale)' : 'var(--cwm-gray-200)',
                color: on ? 'var(--cwm-blue-deep)' : 'var(--cwm-gray-500)',
                fontSize: 11, fontWeight: 900,
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.26, ease: EASE }}
        >
          {activeTab === 'connections' && (
            <ConnectionsTab connections={connections} onRemove={removeConnection} onInvite={() => setInviteModalOpen(true)} />
          )}
          {activeTab === 'invites' && (
            <InvitesTab invitesIn={invitesIn} invitesOut={invitesOut}
                        onAccept={acceptInvite} onDecline={declineInvite}
                        onSend={() => setInviteModalOpen(true)} />
          )}
          {activeTab === 'discovery' && (
            <DiscoveryTab suggestions={suggestions} onInvite={inviteSuggestion} />
          )}
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {inviteModalOpen && (
          <InviteModal onCancel={() => setInviteModalOpen(false)} onSend={sendInvite} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============ TABS ============

function ConnectionsTab({ connections, onRemove, onInvite }) {
  if (!connections.length) {
    return <EmptyTab
      icon="people"
      title="No connections yet"
      body="Start building your table by inviting friends and family who also dine with allergies or preferences."
      cta="Send your first invite" onAction={onInvite}
    />;
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
      {connections.map((c, i) => (
        <ConnectionCard key={c.id} conn={c} index={i} onRemove={() => onRemove(c)} />
      ))}
    </div>
  );
}

function InvitesTab({ invitesIn, invitesOut, onAccept, onDecline, onSend }) {
  if (!invitesIn.length && !invitesOut.length) {
    return <EmptyTab
      icon="mail"
      title="No pending invites"
      body="When someone invites you or you invite someone, it'll show up here until they respond."
      cta="Send an invite" onAction={onSend}
    />;
  }
  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {invitesIn.length > 0 && (
        <section>
          <div className="cwm-eyebrow" style={{ marginBottom: 8 }}>INCOMING ({invitesIn.length})</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {invitesIn.map((inv, i) => (
              <IncomingInviteCard key={inv.id} invite={inv} index={i}
                                  onAccept={() => onAccept(inv)} onDecline={() => onDecline(inv)} />
            ))}
          </div>
        </section>
      )}
      {invitesOut.length > 0 && (
        <section>
          <div className="cwm-eyebrow" style={{ marginBottom: 8 }}>SENT ({invitesOut.length})</div>
          <div style={{ display: 'grid', gap: 10 }}>
            {invitesOut.map((inv, i) => (
              <OutgoingInviteCard key={inv.id} invite={inv} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function DiscoveryTab({ suggestions, onInvite }) {
  if (!suggestions.length) {
    return <EmptyTab
      icon="compass"
      title="No suggestions right now"
      body="We suggest diners with overlapping dietary needs nearby. Check back after you share a few reviews."
    />;
  }
  return (
    <div>
      <div className="cwm-eyebrow" style={{ marginBottom: 10 }}>
        DINERS LIKE YOU ({suggestions.length})
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {suggestions.map((s, i) => (
          <SuggestionCard key={s.id} sug={s} index={i} onInvite={() => onInvite(s)} />
        ))}
      </div>
    </div>
  );
}

// ============ CARDS ============

function ConnectionCard({ conn, index, onRemove }) {
  const hue = hueFor(conn.name);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: index * 0.04, ease: EASE }}
      whileHover={{ y: -2 }}
      className="cwm-sheet"
      style={{ padding: 18 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: hue.bg, color: '#fff',
          display: 'grid', placeItems: 'center',
          fontSize: 16, fontWeight: 900, letterSpacing: '-0.02em',
          boxShadow: '0 6px 14px rgba(13,71,161,0.18)', flexShrink: 0,
        }}>
          {initials(conn.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {conn.name}
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
            {conn.relation} · {conn.visits_together} shared visit{conn.visits_together === 1 ? '' : 's'}
          </div>
        </div>
        <button type="button" onClick={onRemove} aria-label="remove" style={iconBtn}>
          <TrashIcon size={13} />
        </button>
      </div>
      {conn.dietary?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
          {conn.dietary.map((d) => (
            <span key={d} style={{
              padding: '3px 9px', borderRadius: 999,
              background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
              fontSize: 10.5, fontWeight: 800,
            }}>{d}</span>
          ))}
        </div>
      )}
      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>
        Last dined {conn.last}
      </div>
    </motion.article>
  );
}

function IncomingInviteCard({ invite, index, onAccept, onDecline }) {
  const hue = hueFor(invite.from);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: 18 }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: hue.bg, color: '#fff',
          display: 'grid', placeItems: 'center',
          fontSize: 14, fontWeight: 900, flexShrink: 0,
        }}>
          {initials(invite.from)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 900, color: 'var(--cwm-ink)' }}>
            {invite.from} wants to connect
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
            {invite.email} · {invite.created}
          </div>
        </div>
      </div>
      {invite.message && (
        <p style={{
          margin: '0 0 12px', padding: '10px 14px',
          background: 'var(--cwm-gray-50)',
          borderLeft: '3px solid var(--cwm-blue)',
          borderRadius: '0 12px 12px 0',
          fontSize: 13, fontWeight: 500, color: 'var(--cwm-gray-700)', fontStyle: 'italic', lineHeight: 1.5,
        }}>"{invite.message}"</p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={onAccept} className="cwm-btn" style={{ flex: 1, justifyContent: 'center', padding: '10px 14px' }}>
          <CheckIcon size={12} /> Accept
        </button>
        <button type="button" onClick={onDecline} className="cwm-btn-secondary" style={{ padding: '10px 14px' }}>
          Decline
        </button>
      </div>
    </motion.article>
  );
}

function OutgoingInviteCard({ invite, index }) {
  const hue = hueFor(invite.to);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04, ease: EASE }}
      className="cwm-sheet"
      style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 12,
        background: hue.bg, color: '#fff',
        display: 'grid', placeItems: 'center',
        fontSize: 13, fontWeight: 900, flexShrink: 0, opacity: 0.7,
      }}>
        {initials(invite.to)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--cwm-ink)' }}>
          Awaiting reply from {invite.to}
        </div>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
          {invite.email} · sent {invite.created}
        </div>
      </div>
      <span style={{
        padding: '4px 10px', borderRadius: 999,
        background: 'var(--cwm-amber-light)', color: 'var(--cwm-amber-text)',
        fontSize: 10.5, fontWeight: 900, letterSpacing: '0.04em',
      }}>PENDING</span>
    </motion.article>
  );
}

function SuggestionCard({ sug, index, onInvite }) {
  const hue = hueFor(sug.name);
  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: index * 0.04, ease: EASE }}
      whileHover={{ y: -2 }}
      className="cwm-sheet"
      style={{ padding: 18 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: hue.bg, color: '#fff',
          display: 'grid', placeItems: 'center',
          fontSize: 15, fontWeight: 900, flexShrink: 0,
        }}>
          {initials(sug.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.005em' }}>
            {sug.name}
          </div>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--cwm-gray-500)', marginTop: 2 }}>
            {sug.city} · {sug.mutuals} mutual{sug.mutuals === 1 ? '' : 's'}
          </div>
        </div>
      </div>
      {sug.overlap?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div className="cwm-eyebrow" style={{ fontSize: 9.5, marginBottom: 4 }}>OVERLAPS WITH YOU</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {sug.overlap.map((o) => (
              <span key={o} style={{
                padding: '3px 9px', borderRadius: 999,
                background: 'var(--cwm-green-light)', color: 'var(--cwm-green-text)',
                fontSize: 10.5, fontWeight: 800,
              }}>✓ {o}</span>
            ))}
          </div>
        </div>
      )}
      <button type="button" onClick={onInvite} className="cwm-btn"
              style={{ width: '100%', justifyContent: 'center', padding: '9px 14px', fontSize: 13 }}>
        <PlusIcon size={12} /> Invite to your table
      </button>
    </motion.article>
  );
}

// ============ DIALOG ============

function InviteModal({ onCancel, onSend }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const submit = () => {
    const e = email.trim();
    if (!e) { setError('Add their email first.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setError("That email doesn't look right."); return; }
    onSend({ email: e, message: message.trim() });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(10,37,64,0.55)', backdropFilter: 'blur(6px)',
        display: 'grid', placeItems: 'center', padding: 20,
      }}
    >
      <motion.div
        initial={{ scale: 0.96, y: 10, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 10, opacity: 0 }}
        transition={{ duration: 0.28, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 28, padding: 24, maxWidth: 460, width: '100%', boxShadow: 'var(--cwm-shadow-hero)' }}
      >
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <div className="cwm-eyebrow-badge" style={{ marginBottom: 8 }}>
              <MailIcon size={12} /> SEND INVITE
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 900, margin: 0, color: 'var(--cwm-ink)', letterSpacing: '-0.01em' }}>
              Grow your table
            </h2>
          </div>
          <button onClick={onCancel} aria-label="close"
                  style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--cwm-gray-100)', border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 900, color: 'var(--cwm-gray-700)' }}>×</button>
        </header>

        <div style={{ marginBottom: 12 }}>
          <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>EMAIL</div>
          <input value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="their@email.com" type="email"
                 style={{
                   width: '100%', boxSizing: 'border-box',
                   padding: '12px 16px', borderRadius: 14,
                   border: '1.5px solid var(--cwm-blue-light)',
                   background: '#F7FBFF', color: 'var(--cwm-ink)',
                   fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 600,
                   outline: 'none',
                 }}/>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-700)' }}>PERSONAL NOTE (OPTIONAL)</div>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder="Hey! Want to share restaurant picks? I trust your gluten-free radar..." rows={3}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      padding: '12px 16px', borderRadius: 14,
                      border: '1.5px solid var(--cwm-blue-light)',
                      background: '#F7FBFF', color: 'var(--cwm-ink)',
                      fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 500, lineHeight: 1.5,
                      outline: 'none', resize: 'vertical',
                    }}/>
        </div>

        {error && <div style={{ fontSize: 13, color: 'var(--cwm-red-text)', fontWeight: 700, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="cwm-btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="cwm-btn" onClick={submit}>
            <MailIcon size={12} /> Send invite
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============ SHARED ============

function StatTile({ label, value }) {
  return (
    <div style={{
      padding: '12px 14px', borderRadius: 14,
      background: 'rgba(255,255,255,0.18)',
      backdropFilter: 'blur(6px)',
      border: '1px solid rgba(255,255,255,0.22)', color: '#fff',
    }}>
      <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.8 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: '-0.02em', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function ActionPill({ tone, icon, label, onClick }) {
  const tones = {
    blue:   { bg: 'var(--cwm-blue-pale)',   fg: 'var(--cwm-blue-deep)',  solid: 'var(--cwm-blue-deep)' },
    green:  { bg: 'var(--cwm-green-light)', fg: 'var(--cwm-green-text)', solid: 'var(--cwm-green)' },
    orange: { bg: 'var(--cwm-amber-light)', fg: 'var(--cwm-amber-text)', solid: 'var(--cwm-amber)' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <motion.button
      type="button" onClick={onClick}
      whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 10,
        padding: '14px 18px', borderRadius: 18,
        background: '#fff', color: t.fg,
        border: `1.5px solid ${t.solid}22`,
        fontFamily: 'var(--cwm-font)', fontSize: 13.5, fontWeight: 900,
        cursor: 'pointer',
        boxShadow: `0 6px 14px ${t.solid}11`,
        transition: 'all 160ms ease',
      }}>
      <span style={{
        width: 34, height: 34, borderRadius: 10,
        background: t.bg, color: t.fg,
        display: 'grid', placeItems: 'center',
      }}>
        <ActionIcon name={icon} size={16} />
      </span>
      {label}
    </motion.button>
  );
}

function EmptyTab({ icon, title, body, cta, onAction }) {
  return (
    <div style={{
      padding: '44px 24px', textAlign: 'center',
      borderRadius: 22, background: 'var(--cwm-gray-50)',
      border: '1.5px dashed var(--cwm-gray-300)',
    }}>
      <div style={{
        width: 60, height: 60, margin: '0 auto 14px', borderRadius: '50%',
        background: '#fff', color: 'var(--cwm-blue-deep)',
        display: 'grid', placeItems: 'center',
        border: '1.5px dashed var(--cwm-blue-light)',
      }}>
        <TabIcon name={icon} size={24} />
      </div>
      <h3 style={{ fontSize: 18, fontWeight: 900, color: 'var(--cwm-ink)', margin: '0 0 6px', letterSpacing: '-0.005em' }}>
        {title}
      </h3>
      <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--cwm-gray-700)', maxWidth: 400, margin: '0 auto 18px', lineHeight: 1.5 }}>
        {body}
      </p>
      {cta && (
        <button type="button" onClick={onAction} className="cwm-btn">
          <PlusIcon size={12} /> {cta}
        </button>
      )}
    </div>
  );
}

function LoadingBlock() {
  return (
    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
      <div style={{ width: 40, height: 40, margin: '0 auto 12px', border: '4px solid var(--cwm-blue-light)', borderTopColor: 'var(--cwm-blue-deep)', borderRadius: '50%', animation: 'cwmSpin 900ms linear infinite' }}/>
      <style>{`@keyframes cwmSpin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--cwm-gray-500)' }}>Loading your table…</div>
    </div>
  );
}

// ============ STYLES + ICONS ============

const iconBtn = {
  width: 30, height: 30, borderRadius: 999,
  background: '#fff', color: 'var(--cwm-gray-500)',
  border: '1px solid var(--cwm-gray-200)',
  cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0,
};

function PeopleIcon({ size = 20 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11a3 3 0 100-6 3 3 0 000 6zM17 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM3 19c0-3 3-5 6-5s6 2 6 5M16 19c0-2 1.5-4 4-4"/></svg>; }
function PlusIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function CheckIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>; }
function TrashIcon({ size = 13 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }
function MailIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>; }
function TabIcon({ name, size = 14 }) {
  const paths = {
    people:  'M9 11a3 3 0 100-6 3 3 0 000 6zM17 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM3 19c0-3 3-5 6-5s6 2 6 5M16 19c0-2 1.5-4 4-4',
    mail:    'M3 5h18v14H3zM3 7l9 6 9-6',
    compass: 'M12 3a9 9 0 100 18 9 9 0 000-18zM16 8l-2 6-6 2 2-6z',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.people}/></svg>;
}
function ActionIcon({ name, size = 16 }) {
  const paths = {
    plus:   'M12 5v14M5 12h14',
    chat:   'M21 12a9 9 0 01-4.5 7.8L17 22l-4-2a9 9 0 119-8z',
    shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={paths[name] || paths.plus}/></svg>;
}
